import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["grid", "input", "empty", "status", "pending", "lightbox", "lightboxImage", "overflowPhoto", "showMore"]
  static values = {
    uploadUrl: String,
    csrfToken: String,
  }

  connect() {
    this.pendingUrls = []
    this.uploading = false
  }

  disconnect() {
    this.revokePendingUrls()
  }

  async pick(event) {
    const files = Array.from(event.target.files || [])
    if (!files.length || this.uploading) return

    this.showPendingPreviews(files)
    await this.uploadFiles(files, event.target)
  }

  openLightbox(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasLightboxTarget || !this.hasLightboxImageTarget) return

    const { url, alt } = event.params
    this.lightboxImageTarget.src = url
    this.lightboxImageTarget.alt = alt || ""
    this.lightboxTarget.showModal()
  }

  closeLightbox() {
    if (!this.hasLightboxTarget) return
    this.lightboxTarget.close()
    if (this.hasLightboxImageTarget) this.lightboxImageTarget.src = ""
  }

  closeLightboxOnBackdrop(event) {
    if (event.target === this.lightboxTarget) this.closeLightbox()
  }

  showAll() {
    this.overflowPhotoTargets.forEach((el) => el.classList.remove("hidden"))
    if (this.hasShowMoreTarget) this.showMoreTarget.remove()
  }

  async remove(event) {
    event.preventDefault()
    event.stopPropagation()

    const attachmentId =
      event.params.attachmentId || event.currentTarget.dataset.listingPhotosAttachmentIdParam
    if (!attachmentId || this.uploading) return
    const message = event.currentTarget.dataset.confirmMessage
    if (message && !window.confirm(message)) return

    const button = event.currentTarget
    button.disabled = true

    this.setStatus("removing")
    try {
      const response = await fetch(`${this.uploadUrlValue}/${attachmentId}`, {
        method: "DELETE",
        headers: this.requestHeaders(),
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error("remove failed")
      const html = await response.text()
      this.replaceGrid(html)
      this.notifyChecklist()
    } catch {
      this.setStatus("error")
      button.disabled = false
    } finally {
      if (!this.uploading) this.setStatus("")
    }
  }

  async uploadFiles(files, input) {
    this.uploading = true
    this.setStatus("uploading")

    const formData = new FormData()
    files.forEach((file) => formData.append("photos[]", file))

    const labelSelects = this.pendingTarget?.querySelectorAll("[data-pending-label]") || []
    labelSelects.forEach((select) => {
      if (select.value) formData.append("labels[]", select.value)
    })

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: this.requestHeaders(),
        body: formData,
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error("upload failed")

      const html = await response.text()
      this.replaceGrid(html)
      if (input) input.value = ""
      this.clearPending()
      this.notifyChecklist()
    } catch {
      this.setStatus("error")
    } finally {
      this.uploading = false
      if (!this.statusTarget?.textContent) this.setStatus("")
    }
  }

  showPendingPreviews(files) {
    if (!this.hasPendingTarget) return

    this.revokePendingUrls()
    this.pendingTarget.innerHTML = ""
    this.pendingTarget.classList.remove("hidden")

    files.forEach((file, index) => {
      const url = URL.createObjectURL(file)
      this.pendingUrls.push(url)

      const card = document.createElement("div")
      card.className = "min-w-0 overflow-hidden rounded-xl border border-primary/30 bg-surface flex flex-col"
      card.innerHTML = `
        <div class="relative aspect-[4/3] bg-bg">
          <img src="${url}" alt="" class="h-full w-full object-cover" />
          <span class="absolute left-1.5 top-1.5 rounded-md bg-primary/90 px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wide text-white">Uploading</span>
        </div>
        <div class="border-t border-border p-1.5">
          <select class="h-7 w-full rounded-lg border border-border bg-bg px-1.5 text-[0.6875rem] font-medium text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-pending-label data-index="${index}">
            ${this.labelOptionsHtml()}
          </select>
        </div>
      `
      this.pendingTarget.appendChild(card)
    })

    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
  }

  labelOptionsHtml() {
    const template = this.element.querySelector("[data-photo-label-template]")
    return template ? template.innerHTML : ""
  }

  replaceGrid(html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")
    const next = doc.getElementById("listing-photo-grid")
    const current = this.element.querySelector("#listing-photo-grid")
    if (!next || !current) return
    current.replaceWith(next)
  }

  clearPending() {
    this.revokePendingUrls()
    if (this.hasPendingTarget) {
      this.pendingTarget.innerHTML = ""
      this.pendingTarget.classList.add("hidden")
    }
  }

  revokePendingUrls() {
    this.pendingUrls.forEach((url) => URL.revokeObjectURL(url))
    this.pendingUrls = []
  }

  requestHeaders() {
    const csrf =
      document.querySelector('meta[name="csrf-token"]')?.content || this.csrfTokenValue
    return {
      Accept: "text/html",
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
    }
  }

  notifyChecklist() {
    this.element.dispatchEvent(new CustomEvent("listing-photos:changed", { bubbles: true }))
  }

  setStatus(key) {
    if (!this.hasStatusTarget) return
    const messages = this.statusTarget.dataset
    this.statusTarget.textContent = messages[key] || ""
    this.statusTarget.classList.toggle("text-negative", key === "error")
  }
}
